import jsPDF from 'jspdf';
import JsBarcode from 'jsbarcode';
import { compareInstanceNumbers } from './instanceNumber';

export interface UnitLabelItem {
  productName: string;
  templateCode: string;
  instanceNumber: string;
}

/** Zebra ZD420 — 2" × 1.25" die-cut, landscape (wide) */
const LABEL_W = 2;
const LABEL_H = 1.25;
const PAGE_FORMAT: [number, number] = [LABEL_W, LABEL_H];
const PAGE_ORIENTATION = 'l' as const;
const ZEBRA_DPI = 203;

const PAD = 0.06;

interface BarcodeRaster {
  dataUrl: string;
  pxW: number;
  pxH: number;
}

function barcodeRaster(value: string, maxWidthPx: number): BarcodeRaster {
  const canvas = document.createElement('canvas');
  let barWidth = 2.5;
  const barHeight = 80;
  const margin = 16;

  const render = (w: number) => {
    JsBarcode(canvas, value, {
      format: 'CODE128',
      width: w,
      height: barHeight,
      displayValue: false,
      margin,
    });
  };

  render(barWidth);
  while (canvas.width > maxWidthPx && barWidth > 1) {
    barWidth -= 0.25;
    render(barWidth);
  }

  return {
    dataUrl: canvas.toDataURL('image/png'),
    pxW: canvas.width,
    pxH: canvas.height,
  };
}

function fitImage(
  img: BarcodeRaster,
  maxW: number,
  maxH: number,
): { w: number; h: number } {
  const aspect = img.pxW / img.pxH;
  let w = maxW;
  let h = w / aspect;
  if (h > maxH) {
    h = maxH;
    w = h * aspect;
  }
  return { w, h };
}

function drawLabel(doc: jsPDF, item: UnitLabelItem, barcode: BarcodeRaster) {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const cw = pageW - PAD * 2;
  let y = PAD + 0.1;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(0, 0, 0);
  (doc.splitTextToSize(item.productName, cw) as string[]).slice(0, 2).forEach((line) => {
    doc.text(line, pageW / 2, y, { align: 'center' });
    y += 0.1;
  });

  y += 0.08;
  doc.setFont('courier', 'bold');
  doc.setFontSize(8);
  doc.text(item.templateCode, pageW / 2, y, { align: 'center' });

  const unitCodeGap = 0.03;
  const unitCodeLineH = 0.09;
  const bcTop = y + 0.06;
  const maxBcH = Math.max(0.2, pageH - PAD - bcTop - unitCodeLineH - unitCodeGap);
  const { w: bcW, h: bcH } = fitImage(barcode, cw, maxBcH);
  const bcX = PAD + (cw - bcW) / 2;
  const bcY = bcTop;
  doc.addImage(barcode.dataUrl, 'PNG', bcX, bcY, bcW, bcH, undefined, 'SLOW');

  doc.setFont('courier', 'bold');
  doc.setFontSize(8);
  doc.text(item.instanceNumber, pageW / 2, bcY + bcH + unitCodeGap, { align: 'center' });
}

function buildUnitLabelPdf(items: UnitLabelItem[]): jsPDF {
  const sorted = [...items].sort((a, b) =>
    compareInstanceNumbers(a.instanceNumber, b.instanceNumber),
  );
  const maxWidthPx = Math.floor((LABEL_W - PAD * 2) * ZEBRA_DPI);
  const barcodes = sorted.map((item) => barcodeRaster(item.instanceNumber, maxWidthPx));
  const doc = new jsPDF({
    orientation: PAGE_ORIENTATION,
    unit: 'in',
    format: PAGE_FORMAT,
    compress: false,
  });

  sorted.forEach((item, i) => {
    if (i > 0) doc.addPage(PAGE_FORMAT, PAGE_ORIENTATION);
    drawLabel(doc, item, barcodes[i]);
  });
  return doc;
}

function printPdf(doc: jsPDF): void {
  const url = URL.createObjectURL(doc.output('blob'));
  const frame = document.createElement('iframe');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:none';
  frame.src = url;
  document.body.appendChild(frame);

  const cleanup = () => {
    URL.revokeObjectURL(url);
    frame.remove();
  };

  frame.onload = () => {
    const win = frame.contentWindow;
    if (!win) {
      cleanup();
      return;
    }
    win.addEventListener('afterprint', cleanup, { once: true });
    win.focus();
    win.print();
    setTimeout(cleanup, 60_000);
  };
}

export function printUnitLabelPdf(items: UnitLabelItem[]): void {
  if (items.length === 0) return;
  printPdf(buildUnitLabelPdf(items));
}
